import { getAllColumns } from '../../../../../components/Table/columns/index';
import { ColumnDefinition } from '../../../../../components/Table/columns/shared/column-types';

interface ColumnListProps {
  columnVisibilityModel?: Record<string, boolean> | null;
  setColumnVisibilityModel: (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => void;
  view: string;
  tab: string;
}

function ColumnList({ columnVisibilityModel, setColumnVisibilityModel, view, tab }: ColumnListProps) {
  const categories = ['GDA', 'VDA', 'Relation', 'DDA', 'Disease', 'Gene', 'Variant', 'Chemical'];

  const safeColumnVisibilityModel = columnVisibilityModel ?? {};
  const displayFragment = Object.keys(safeColumnVisibilityModel).reduce(
    (acc, key) => {
      const column: ColumnDefinition | undefined = getAllColumns().find(col => col.name === key);

      if (column && column.header) {
        let shouldShow = true;
        if (typeof column.show === 'function') {
          shouldShow = column.show({ tab, view, isEvidence: false });
        } else if (column.show !== undefined) {
          shouldShow = column.show;
        }

        if (shouldShow) {
          acc[key] = {
            header: column.header,
            category: column.category,
          };
        }
      }
      return acc;
    },
    {} as Record<string, { header: any; category?: string }>,
  );

  // Group keys by category
  const columnsByCategory = categories.map(category => ({
    category,
    keys: Object.entries(displayFragment)
      .filter(([_, { category: cat }]) => cat === category)
      .map(([key]) => key),
  }));

  function handleChange({
    key = '',
    toggleAll = false,
    category = '',
  }: {
    key?: string;
    toggleAll?: boolean;
    category?: string;
  }) {
    setColumnVisibilityModel(prevItems => {
      const prev = prevItems ?? {};
      if (toggleAll && category) {
        const updated = { ...prev };
        const keysInCategory = Object.entries(displayFragment)
          .filter(([_, { category: cat }]) => cat === category)
          .map(([colKey]) => colKey);

        const allSelected = keysInCategory.every(colKey => updated[colKey]);

        keysInCategory.forEach(colKey => {
          updated[colKey] = !allSelected;
        });

        return updated;
      }

      return {
        ...prev,
        [key]: !prev[key],
      };
    });
  }

  return (
    <>
      {columnsByCategory.map(({ category, keys }) => {
        if (keys.length === 0) return null;

        const allSelected = keys.every(key => safeColumnVisibilityModel[key]);

        return (
          <div key={category} className="max-w-lg w-lg rounded shadow-lg overflow-auto column-card">
            <h4 className="font-semibold capitalize mb-3 text-center bg-blue text-white py-2 sticky top-0 z-10">
              {category}
            </h4>
            <div className="category-columns px-4">
              <ul className="grid">
                {keys.map(key => (
                  <li key={key}>
                    <label className="gap-1 column-label flex justify-start">
                      <input
                        type="checkbox"
                        checked={!!safeColumnVisibilityModel[key]}
                        onChange={() => handleChange({ key })}
                        className="custom-checkbox"
                      />
                      <span>{displayFragment[key]?.header}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
            <div className="sticky bottom-0 z-10 text-center bg-white action-buttons column-card-actions">
              <button
                onClick={() => handleChange({ toggleAll: true, category })}
                className="select"
                id={`${category}-toggleAll`}
              >
                {allSelected ? 'Clear All' : 'Select All'}
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
}

export default ColumnList;
